import { toggleClass } from '@utils/ponyFills';
import { useCallback, useEffect, useRef, useState } from 'react';

import AppEventEmitter, { events as AppEventEmitterEvents } from '@utils/appEventEmitter';

import type { BackgroundItem, ParallaxHeaderPropsType } from './types';

const ParallaxHeader: React.FC<ParallaxHeaderPropsType> = ({ pageImageUrl: propPageImageUrl, pageBackgroundImageUrl: propPageBackgroundImageUrl, forceSmall, children, setImageData }) => {
  const [variant, setVariant] = useState('normal');
  const [backgrounds, setBackgrounds] = useState<BackgroundItem[]>([]);
  const [lastHref, setLastHref] = useState('');
  const [pageImageUrl, setPageImageUrl] = useState(propPageImageUrl);
  const [pageBackgroundImageUrl, setPageBackgroundImageUrl] = useState(propPageBackgroundImageUrl);

  const headerIsMountedRef = useRef(false);
  const skipLuminosityCheckRef = useRef(false);
  const tickingRef = useRef(false);
  const backgroundsRef = useRef<BackgroundItem[]>([]);
  const variantRef = useRef(variant);

  useEffect(() => {
    backgroundsRef.current = backgrounds;
  }, [backgrounds]);

  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);

  const getHeaderVariant = useCallback(() => {
    if (forceSmall) {
      return 'small';
    }
    const scrollTop = window.pageYOffset;
    return scrollTop > 80 ? 'small' : 'normal';
  }, [forceSmall]);

  const setCollectiveClass = useCallback((collection: (HTMLElement | null)[], className: string, condition: boolean) => {
    for (const elm of collection) {
      if (elm) {
        toggleClass(elm, className, condition);
      }
    }
  }, []);

  const setHeaderPosition = useCallback(() => {
    const scrollTop = window.pageYOffset;
    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const topLine = windowWidth > 968 ? 21 : 0;

    for (const item of backgroundsRef.current) {
      const elmScrollTop = item.elementTop - scrollTop;
      const collection = [item.element, item.sectionLayer.element, item.overlayLayer.element, item.backgroundLayer.element];

      const overlayHeight = topLine > 0 ? Math.max(0, Math.min(21, 42 - elmScrollTop)) : 0;
      if (item.overlayLayer.element) {
        item.overlayLayer.element.style.height = `${overlayHeight}px`;
        item.overlayLayer.element.style.transform = `translateY( ${-overlayHeight}px )`;
        item.overlayLayer.element.style.visibility = overlayHeight === 0 ? 'none' : 'block';
      }

      setCollectiveClass(collection, 'is-fixed', elmScrollTop < topLine);
      setCollectiveClass(collection, 'is-small', elmScrollTop < -80);
    }
  }, [setCollectiveClass]);

  const setHeaderVariant = useCallback(() => {
    const newVariant = getHeaderVariant();
    if (variantRef.current !== newVariant && headerIsMountedRef.current) {
      setVariant(newVariant);
    }
  }, [getHeaderVariant]);

  const onScrollFired = useCallback(() => {
    if (!tickingRef.current) {
      window.requestAnimationFrame(() => {
        setHeaderVariant();
        setHeaderPosition();
        tickingRef.current = false;
      });
      tickingRef.current = true;
    }
  }, [setHeaderVariant, setHeaderPosition]);

  const collectAvailableBackgrounds = useCallback((): BackgroundItem[] => {
    const backgroundCollection = document.querySelectorAll('.SectionBackground');
    const newBackgrounds: BackgroundItem[] = [];

    for (const i of backgroundCollection) {
      const backgroundElement = i as HTMLElement;
      // Skip detached or invisible elements (stale refs from unmounted pages)
      if (!backgroundElement.isConnected || backgroundElement.getBoundingClientRect().width === 0) {
        continue;
      }

      newBackgrounds.push({
        url: backgroundElement.getAttribute('data-url') || '',
        backgroundUrl: backgroundElement.getAttribute('data-background-url') || '',
        element: backgroundElement,
        elementTop: backgroundElement.offsetTop,
        sectionLayer: {
          element: null,
          left: 0,
          top: backgroundElement.offsetTop,
        },
        overlayLayer: {
          element: null,
          left: 0,
          top: backgroundElement.offsetTop,
        },
        backgroundLayer: {
          element: null,
          left: 0,
          top: backgroundElement.offsetTop,
        },
      });
    }
    return [...newBackgrounds];
  }, []);

  const updateBackgroundOffsets = useCallback(() => {
    backgroundsRef.current = backgroundsRef.current.map((item) => {
      item.elementTop = item.element.offsetTop;
      item.sectionLayer.top = item.elementTop;
      item.overlayLayer.top = item.elementTop;
      item.backgroundLayer.top = item.elementTop;
      return item;
    });
  }, []);

  const positionateHelperBackgrounds = useCallback(() => {
    // Filter out detached elements before positioning
    const connectedBackgrounds = backgroundsRef.current.filter((item) => item.element.isConnected && item.element.getBoundingClientRect().width > 0);
    if (connectedBackgrounds.length !== backgroundsRef.current.length) {
      backgroundsRef.current = connectedBackgrounds;
      setBackgrounds([...connectedBackgrounds]);
      return;
    }

    updateBackgroundOffsets();

    try {
      for (const item of backgroundsRef.current) {
        const boundingRect = item.element.getBoundingClientRect();
        if (item.sectionLayer.element) {
          const sectionStyles = item.sectionLayer.element.style;
          sectionStyles.left = `${boundingRect.left}px`;
          sectionStyles.top = `${item.element.offsetTop}px`;
          setTimeout(() => {
            sectionStyles.opacity = '1';
          }, 100);
        }
        if (item.overlayLayer.element) {
          const overlayStyles = item.overlayLayer.element.style;
          overlayStyles.top = `${item.element.offsetTop}px`;
        }
        if (item.backgroundLayer.element) {
          const backgroundStyles = item.backgroundLayer.element.style;
          backgroundStyles.top = `${item.element.offsetTop}px`;
          setTimeout(() => {
            backgroundStyles.opacity = '1';
          }, 100);
        }
      }

      onScrollFired();
    } catch (e) {
      window.requestAnimationFrame(() => {
        setBackgrounds(collectAvailableBackgrounds());
      });
    }
  }, [updateBackgroundOffsets, onScrollFired, collectAvailableBackgrounds]);

  const updateComponent = useCallback(() => {
    if (lastHref === window.location.href) {
      return;
    }

    requestAnimationFrame(() => {
      if (!headerIsMountedRef.current) {
        return;
      }

      setLastHref(window.location.href);
      setVariant(getHeaderVariant());
      setBackgrounds(collectAvailableBackgrounds());
      setHeaderPosition();
    });
  }, [lastHref, getHeaderVariant, collectAvailableBackgrounds, setHeaderPosition]);

  const changeImage = useCallback((newPageImageUrl: string, newPageBackgroundImageUrl: string) => {
    setPageImageUrl(newPageImageUrl);
    setPageBackgroundImageUrl(newPageBackgroundImageUrl);
  }, []);

  const getImageLuminosity = useCallback((): void => {
    const url = process.env.HOST + pageImageUrl;

    if (skipLuminosityCheckRef.current) {
      skipLuminosityCheckRef.current = false;
      return;
    }

    const nthPixel = 3;

    window.requestAnimationFrame(() => {
      const wrapper = document.body.querySelector('.-InnerWrapper');
      const heading = document.body.querySelector('.Header-heading h1');

      if (!wrapper || !heading) {
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const headingRect = heading.getBoundingClientRect();

      if (headingRect.width === 0) {
        return;
      }

      const p = new Promise<{ r: number; g: number; b: number }>((resolve, reject) => {
        const img = document.createElement('img');
        img.src = url;

        img.addEventListener('load', () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject('Canvas context unavailable');
            return;
          }
          const rgb = { r: 0, g: 0, b: 0 };

          const xPos = Math.round(img.naturalWidth / 2 - wrapperRect.width / 2 + headingRect.left);
          const yPos = Math.round(headingRect.top - wrapperRect.top);

          canvas.width = headingRect.width;
          canvas.height = headingRect.height;

          ctx.drawImage(img, -xPos, -yPos);

          try {
            const data = ctx.getImageData(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < data.data.length; i += nthPixel * 4) {
              rgb.r += data.data[i];
              rgb.g += data.data[i + 1];
              rgb.b += data.data[i + 2];
            }

            for (const c of Object.keys(rgb)) {
              (rgb as any)[c] /= data.data.length / (nthPixel * 4);
            }
          } catch (e) {
            reject('CORS error');
          }

          resolve(rgb);
        });
      });

      p.then((data) => {
        skipLuminosityCheckRef.current = true;
        setImageData(data);
      }).catch(() => {
        // CORS error - silently ignore
      });
    });
  }, [pageImageUrl, setImageData]);

  const appendElement = useCallback((index: number, elementName: string, element: HTMLElement | null) => {
    if (!backgroundsRef.current[index] || !backgroundsRef.current[index][elementName]) {
      return;
    }
    backgroundsRef.current[index][elementName].element = element;
  }, []);

  // Mount effect - setup event listeners
  useEffect(() => {
    headerIsMountedRef.current = true;

    AppEventEmitter.on(AppEventEmitterEvents.HEADER_CHANGE_IMAGE, changeImage);
    document.addEventListener('scroll', onScrollFired);
    window.addEventListener('resize', positionateHelperBackgrounds);

    updateComponent();

    return () => {
      AppEventEmitter.removeListener(AppEventEmitterEvents.HEADER_CHANGE_IMAGE, changeImage);
      document.removeEventListener('scroll', onScrollFired);
      window.removeEventListener('resize', positionateHelperBackgrounds);
      headerIsMountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // componentDidUpdate equivalent - runs on every render
  useEffect(() => {
    updateComponent();
    getImageLuminosity();
  });

  // Sync props when they change on navigation
  useEffect(() => {
    setPageImageUrl(propPageImageUrl);
  }, [propPageImageUrl]);

  useEffect(() => {
    setPageBackgroundImageUrl(propPageBackgroundImageUrl);
  }, [propPageBackgroundImageUrl]);

  const renderAdditionalBackgrounds = () => {
    setTimeout(() => requestAnimationFrame(positionateHelperBackgrounds), 0);
    return (
      <div>
        {backgrounds.map((obj, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: key includes lastHref to force re-render on navigation
          <div className="ParallaxHeader-helper" key={lastHref + index}>
            <div
              style={{
                backgroundImage: `url(${obj.url})`,
                backgroundPosition: 'center -21px',
              }}
              className={
                '-HelperSectionLayer absolute left-0 top-0 z-3000 h-[440px] w-full max-w-[1400px] bg-no-repeat bg-top opacity-0 transition-[height,opacity] duration-500 [&.is-fixed]:fixed [&.is-fixed]:top-[21px]! [&.is-fixed]:z-2990 [&.is-small]:h-[165px] max-[1500px]:max-w-[960px] max-[1100px]:h-[348px] max-[968px]:max-w-[968px] max-[968px]:[&.is-fixed]:top-0! max-[700px]:h-[110px]! max-[700px]:bg-center'
              }
              ref={(elm) => appendElement(index, 'sectionLayer', elm)}
            />
            <div
              className="-HelperOverlayLayer fixed left-0 top-[21px]! z-4020 h-[21px] w-full bg-no-repeat bg-top transition-none"
              style={{ backgroundImage: `url(${obj.backgroundUrl})` }}
              ref={(elm) => appendElement(index, 'overlayLayer', elm)}
            />
            <div
              className="-HelperBackgroundLayer absolute left-0 top-0 z-10 mt-[-20px] h-[480px] w-full bg-no-repeat bg-top opacity-100 transition-[height,opacity] duration-500 [&.is-fixed]:fixed [&.is-fixed]:top-[21px]! [&.is-fixed]:z-9 [&.is-small]:h-[207px] max-[1100px]:h-[388px] max-[968px]:hidden"
              style={{ backgroundImage: `url(${obj.backgroundUrl})` }}
              ref={(elm) => appendElement(index, 'backgroundLayer', elm)}
            />
          </div>
        ))}
      </div>
    );
  };

  const url = process.env.HOST + pageImageUrl;
  const backgroundUrl = process.env.HOST + pageBackgroundImageUrl;
  const displayVariant = forceSmall ? 'small' : variant;

  return (
    <div className={`ParallaxHeader -parallaxHeader-${displayVariant}`}>
      <div
        className={`-BackgroundLayer fixed left-0 top-0 z-0 m-auto w-full bg-no-repeat bg-top transition-[height] duration-500 ${
          displayVariant === 'small' ? 'h-[207px] max-[1100px]:h-[186px]! max-[700px]:h-[110px]!' : 'h-[482px] max-[1100px]:h-[367px]! max-[700px]:h-[110px]!'
        }`}
        style={{ backgroundImage: `url(${backgroundUrl})` }}
      />
      <div className="ParallaxHeader-backgrounds">{renderAdditionalBackgrounds()}</div>
      <div
        className={`-Wrapper fixed left-0 top-0 z-1000 m-auto w-full transition-[height] duration-500 ${
          displayVariant === 'small' ? 'h-[186px] max-[1100px]:h-[186px]! max-[700px]:h-[110px]!' : 'h-[466px] max-[1100px]:h-[367px]! max-[700px]:h-[110px]!'
        }`}
      >
        <div className="-OverlayLine relative z-20 h-[21px] w-full bg-page-bg bg-no-repeat bg-top max-[968px]:hidden" style={{ backgroundImage: `url(${backgroundUrl})` }} />
        <div
          className={`-InnerWrapper relative mx-auto w-full max-w-[1400px] bg-[#fafafa] bg-no-repeat transition-[height] duration-500 max-[1500px]:max-w-[960px] max-[968px]:max-w-[968px] max-[700px]:h-[110px]! ${
            displayVariant === 'small' ? 'h-[165px] max-[1100px]:h-[165px]' : 'h-[444px] max-[1100px]:h-[348px]'
          }`}
          style={{
            backgroundImage: `url(${url})`,
            backgroundPosition: 'center -21px',
          }}
        />
      </div>

      <div
        className={`-MenuWrapper fixed left-0 top-0 z-3100 m-auto w-full transition-[height] duration-500 ${
          displayVariant === 'small' ? 'h-[165px] max-[1100px]:h-[165px] max-[700px]:h-[110px]!' : 'h-[444px] max-[1100px]:h-[348px] max-[700px]:h-[110px]!'
        }`}
      >
        <div className="-MenuInnerWrapper relative mx-auto mt-[21px] flex h-full max-w-[1400px] flex-col items-center max-[1500px]:max-w-[960px] max-[968px]:mt-0 max-[968px]:max-w-[968px] max-[700px]:justify-center">
          {children}
        </div>
      </div>
    </div>
  );
};

export { ParallaxHeader };
